/**
 * Interactive Pet Dental Health Quiz
 * Engaging assessment that educates and converts visitors
 */

import React, { useEffect, useState } from 'react';
import BookAppointmentLink from './BookAppointmentLink';
import { trackDentalQuizComplete, trackPhoneClick } from '../utils/analytics';

interface QuizQuestion {
  id: number;
  question: string;
  description?: string;
  options: {
    text: string;
    score: number;
    icon: string;
  }[];
}

interface QuizResult {
  range: [number, number];
  level: 'excellent' | 'good' | 'concerning' | 'urgent';
  title: string;
  description: string;
  recommendation: string;
  color: string;
  bgColor: string;
  icon: string;
}

const questions: QuizQuestion[] = [
  {
    id: 1,
    question: "How does your pet's breath smell?",
    description: "Be honest - we won't judge!",
    options: [
      { text: "Fresh or neutral", score: 0, icon: "😊" },
      { text: "Slightly off", score: 1, icon: "😐" },
      { text: "Noticeably bad", score: 2, icon: "😷" },
      { text: "Makes me turn away", score: 3, icon: "🤢" },
    ],
  },
  {
    id: 2,
    question: "When did your pet last have a professional dental cleaning?",
    options: [
      { text: "Within the last year", score: 0, icon: "✅" },
      { text: "1-2 years ago", score: 1, icon: "📅" },
      { text: "More than 2 years ago", score: 2, icon: "⏰" },
      { text: "Never / I don't know", score: 3, icon: "❓" },
    ],
  },
  {
    id: 3,
    question: "Have you noticed any tartar (yellow/brown buildup) on their teeth?",
    options: [
      { text: "No, teeth look clean", score: 0, icon: "✨" },
      { text: "A little near the gumline", score: 1, icon: "🔍" },
      { text: "Yes, clearly visible", score: 2, icon: "👀" },
      { text: "Heavy buildup / can't see teeth", score: 3, icon: "😬" },
    ],
  },
  {
    id: 4,
    question: "How do their gums look?",
    description: "Gently lift their lip to check",
    options: [
      { text: "Pink and healthy", score: 0, icon: "💗" },
      { text: "Slightly red at the gumline", score: 1, icon: "🔴" },
      { text: "Red, swollen, or bleeding", score: 2, icon: "🩸" },
      { text: "I can't or haven't checked", score: 2, icon: "🤷" },
    ],
  },
  {
    id: 5,
    question: "How is your pet eating?",
    options: [
      { text: "Eating normally, no issues", score: 0, icon: "😋" },
      { text: "Sometimes drops food or eats slowly", score: 1, icon: "🍽️" },
      { text: "Prefers soft food / one side chewing", score: 2, icon: "😣" },
      { text: "Difficulty eating / weight loss", score: 3, icon: "⚠️" },
    ],
  },
  {
    id: 6,
    question: "How old is your pet?",
    options: [
      { text: "Under 2 years", score: 0, icon: "🐕" },
      { text: "2-5 years", score: 1, icon: "🐕‍🦺" },
      { text: "5-8 years", score: 2, icon: "🦮" },
      { text: "Over 8 years", score: 3, icon: "🐕‍🦺" },
    ],
  },
];

const results: QuizResult[] = [
  {
    range: [0, 4],
    level: 'excellent',
    title: "Great Dental Health! 🌟",
    description: "Your pet's dental health appears to be in excellent condition! Keep up the great work with regular checkups.",
    recommendation: "Schedule an annual dental exam to maintain this excellent health and catch any issues early.",
    color: "text-green-600",
    bgColor: "from-green-50 to-emerald-50",
    icon: "🏆",
  },
  {
    range: [5, 8],
    level: 'good',
    title: "Good, With Room to Improve 👍",
    description: "Your pet's dental health is decent, but there are some early warning signs that shouldn't be ignored.",
    recommendation: "A professional dental exam within the next few months can prevent these minor issues from becoming major problems.",
    color: "text-blue-600",
    bgColor: "from-blue-50 to-cyan-50",
    icon: "👍",
  },
  {
    range: [9, 13],
    level: 'concerning',
    title: "Attention Needed ⚠️",
    description: "Your pet is showing signs of dental disease that could be causing discomfort and affecting their health.",
    recommendation: "We strongly recommend scheduling a dental consultation within the next 2-4 weeks to prevent further damage.",
    color: "text-orange-600",
    bgColor: "from-orange-50 to-amber-50",
    icon: "⚠️",
  },
  {
    range: [14, 18],
    level: 'urgent',
    title: "Urgent Care Recommended 🚨",
    description: "Your pet is likely experiencing significant dental disease that may be painful and affecting their quality of life.",
    recommendation: "Please call us as soon as possible. Dental disease at this stage can cause serious health problems if left untreated.",
    color: "text-red-600",
    bgColor: "from-red-50 to-rose-50",
    icon: "🚨",
  },
];

const DentalHealthQuiz: React.FC = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [petName, setPetName] = useState('');
  const [petType, setPetType] = useState<'dog' | 'cat' | null>(null);
  const [showIntro, setShowIntro] = useState(true);

  const handleAnswer = (score: number) => {
    setIsAnimating(true);
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setShowResult(true);
      }
      setIsAnimating(false);
    }, 300);
  };

  const totalScore = answers.reduce((a, b) => a + b, 0);
  const result = results.find(r => totalScore >= r.range[0] && totalScore <= r.range[1]) || results[3];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  // Fires the dental_quiz_complete event the moment the result screen renders.
  // "Complete" = user answered every question and the score is shown.
  useEffect(() => {
    if (showResult) {
      trackDentalQuizComplete({
        score: totalScore,
        maxScore: 18,
        level: result.level,
        petType,
      });
    }
  }, [showResult, totalScore, result.level, petType]);

  const startQuiz = () => {
    if (petName && petType) {
      setShowIntro(false);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setShowIntro(true);
    setPetName('');
    setPetType(null);
  };

  // Intro Screen
  if (showIntro) {
    return (
      <section className="section-padding bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8 animate-bounce">
              <span className="text-6xl">🦷</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 font-nantes">
              Pet Dental Health Check
            </h2>
            <p className="text-xl opacity-90 mb-8">
              Take our 60-second quiz to discover your pet's dental health score and get personalized recommendations.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 space-y-6">
              <div>
                <label htmlFor="dental-quiz-pet-name" className="block text-left text-sm font-medium mb-2">
                  What's your pet's name?
                </label>
                <input
                  id="dental-quiz-pet-name"
                  type="text"
                  value={petName}
                  onChange={(e) => setPetName(e.target.value)}
                  placeholder="e.g., Max, Luna, Buddy..."
                  autoComplete="off"
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>

              <div>
                <label className="block text-left text-sm font-medium mb-3">
                  Is {petName || 'your pet'} a dog or cat?
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setPetType('dog')}
                    className={`py-4 rounded-xl font-bold text-lg transition-all ${
                      petType === 'dog'
                        ? 'bg-white text-purple-600 shadow-lg scale-105'
                        : 'bg-white/20 hover:bg-white/30'
                    }`}
                  >
                    🐕 Dog
                  </button>
                  <button
                    onClick={() => setPetType('cat')}
                    className={`py-4 rounded-xl font-bold text-lg transition-all ${
                      petType === 'cat'
                        ? 'bg-white text-purple-600 shadow-lg scale-105'
                        : 'bg-white/20 hover:bg-white/30'
                    }`}
                  >
                    🐱 Cat
                  </button>
                </div>
              </div>

              <button
                onClick={startQuiz}
                disabled={!petName || !petType}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                  petName && petType
                    ? 'bg-white text-purple-600 hover:bg-gray-100 shadow-lg'
                    : 'bg-white/30 cursor-not-allowed'
                }`}
              >
                Start Quiz →
              </button>
            </div>

            <p className="mt-6 text-sm opacity-70">
              <i className="fas fa-lock mr-2"></i>
              Your information is private and never shared.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Result Screen
  if (showResult) {
    return (
      <section className={`section-padding bg-gradient-to-br ${result.bgColor}`}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            {/* Result Card */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className={`p-8 text-center ${
                result.level === 'excellent' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                result.level === 'good' ? 'bg-gradient-to-r from-blue-500 to-cyan-600' :
                result.level === 'concerning' ? 'bg-gradient-to-r from-orange-500 to-amber-600' :
                'bg-gradient-to-r from-red-500 to-rose-600'
              } text-white`}>
                <div className="text-6xl mb-4">{result.icon}</div>
                <h3 className="text-3xl font-bold mb-2">{result.title}</h3>
                <p className="text-lg opacity-90">
                  {petName}'s Dental Health Score: <span className="font-bold">{totalScore}/18</span>
                </p>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                {/* Score visualization */}
                <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all duration-1000 ${
                      result.level === 'excellent' ? 'bg-green-500' :
                      result.level === 'good' ? 'bg-blue-500' :
                      result.level === 'concerning' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${(totalScore / 18) * 100}%` }}
                  />
                </div>

                <p className="text-gray-700 text-lg leading-relaxed">
                  {result.description}
                </p>

                <div className={`p-4 rounded-xl ${
                  result.level === 'excellent' ? 'bg-green-50 border border-green-200' :
                  result.level === 'good' ? 'bg-blue-50 border border-blue-200' :
                  result.level === 'concerning' ? 'bg-orange-50 border border-orange-200' :
                  'bg-red-50 border border-red-200'
                }`}>
                  <h4 className={`font-bold mb-2 ${result.color}`}>
                    <i className="fas fa-lightbulb mr-2"></i>
                    Our Recommendation for {petName}:
                  </h4>
                  <p className="text-gray-700">{result.recommendation}</p>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3 pt-4">
                  <BookAppointmentLink
                    analyticsSource="dental_quiz"
                    className={`block w-full py-4 rounded-xl font-bold text-lg text-center text-white transition-all shadow-lg ${
                      result.level === 'urgent' 
                        ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 animate-pulse'
                        : 'bg-gradient-to-r from-brand-teal to-teal-600 hover:from-teal-700 hover:to-teal-800'
                    }`}
                  >
                    {result.level === 'urgent' ? (
                      <>🚨 Book Urgent Appointment</>
                    ) : result.level === 'concerning' ? (
                      <>📅 Schedule Dental Consultation</>
                    ) : (
                      <>✨ Book {petName}'s Checkup</>
                    )}
                  </BookAppointmentLink>

                  <a
                    href="tel:+19544663775"
                    onClick={() => trackPhoneClick('dental_quiz')}
                    className="block w-full py-4 rounded-xl font-bold text-lg text-center text-brand-teal border-2 border-brand-teal hover:bg-brand-teal hover:text-white transition-all"
                  >
                    <i className="fas fa-phone mr-2"></i>
                    Call Now: (954) 466-3775
                  </a>

                  <button
                    onClick={resetQuiz}
                    className="block w-full py-3 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <i className="fas fa-redo mr-2"></i>
                    Take Quiz Again
                  </button>
                </div>
              </div>
            </div>

            {/* Share prompt */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-3">Know a pet parent who should take this quiz?</p>
              <div className="flex justify-center gap-3">
                <button className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <i className="fab fa-facebook-f"></i>
                </button>
                <button className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
                  <i className="fab fa-whatsapp"></i>
                </button>
                <button className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors">
                  <i className="fab fa-twitter"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Quiz Questions
  const question = questions[currentQuestion];

  return (
    <section className="section-padding bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm opacity-80">Question {currentQuestion + 1} of {questions.length}</span>
              <span className="text-sm opacity-80">{Math.round(progress)}% complete</span>
            </div>
            <div
              className="h-2 bg-white/20 rounded-full overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
              aria-label="Quiz progress"
            >
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <div className={`bg-white rounded-3xl shadow-2xl p-8 transition-all duration-300 ${
            isAnimating ? 'opacity-0 translate-x-10' : 'opacity-100 translate-x-0'
          }`}>
            <div className="text-center mb-8">
              <span className="inline-block bg-purple-100 text-purple-600 px-4 py-1 rounded-full text-sm font-medium mb-4">
                About {petName}
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                {question.question}
              </h3>
              {question.description && (
                <p className="text-gray-500">{question.description}</p>
              )}
            </div>

            <div className="space-y-3">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option.score)}
                  className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left flex items-center gap-4 group"
                >
                  <span className="text-3xl group-hover:scale-110 transition-transform">
                    {option.icon}
                  </span>
                  <span className="text-lg text-gray-700 group-hover:text-purple-600 font-medium">
                    {option.text}
                  </span>
                  <i className="fas fa-chevron-right ml-auto text-gray-300 group-hover:text-purple-500 transition-colors"></i>
                </button>
              ))}
            </div>
          </div>

          {/* Pet avatar */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
              <span className="text-2xl">{petType === 'dog' ? '🐕' : '🐱'}</span>
              <span className="font-medium">{petName}'s dental checkup</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DentalHealthQuiz;



